import { useState, useEffect, useRef } from 'react';
import { BudgetApiClient } from '../api/client';
import type { SearchSuggestion } from '../types';

export interface UseSearchSuggestionsResult {
  suggestions: SearchSuggestion[];
  isLoading: boolean;
}

export function useSearchSuggestions(
  apiClient: BudgetApiClient,
  query: string,
  budgetType: 'E' | 'A' = 'A',
  debounceMs: number = 200
): UseSearchSuggestionsResult {
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Don't fetch if query is too short
    if (!query || query.length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Debounce the request
    timeoutRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController();

      try {
        const results = await apiClient.getSearchSuggestions(query, budgetType);
        setSuggestions(results);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch suggestions:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [apiClient, query, budgetType, debounceMs]);

  return { suggestions, isLoading };
}
